import React, { useState, useEffect } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { utcToLocal, localToUtc } from '../../helpers/timezoneHelper';

interface Period {
  id: number;
  label: string;
  startTime: string; // UTC ISO string — start of this period
}

interface SuggestedTime {
  label: string;
  firstStatTime: string; // UTC ISO string — first stat recorded in this period
}

const PeriodsPage: React.FC = () => {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [gameType, setGameType] = useState<'quarters' | 'halves'>('quarters');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otFlag, setOtFlag] = useState(false);
  const [dirty, setDirty] = useState(false);

  const gameId = sessionManager.getGameId();
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();

  useEffect(() => {
    const fetchGameInfo = async () => {
      try {
        setLoading(true);

        // Fetch season period settings (half/quarter)
        const seasonPeriodResponse = await api.get(API_URLS.GET_SEASON_PERIOD, {
          params: { clientId, seasonId },
        });

        if (!seasonPeriodResponse.data.success) {
          showErrorMsg(seasonPeriodResponse.data.message || 'Failed to fetch season period settings');
          return;
        }

        const isHalfPeriod = !!seasonPeriodResponse.data.data.halfPeriod;
        setGameType(isHalfPeriod ? 'halves' : 'quarters');

        // Fetch game OT flag
        let gameOtFlag = false;
        if (gameId) {
          try {
            const gameResponse = await api.get(`${API_URLS.GET_GAME_BY_ID}/${gameId}`);
            if (gameResponse.data.success && gameResponse.data.data) {
              gameOtFlag = gameResponse.data.data.otFlag || false;
              setOtFlag(gameOtFlag);
            }
          } catch {
            // non-fatal
          }
        }

        // Fetch suggested period start times from statslog data
        let suggestedTimes: SuggestedTime[] = [];
        if (gameId) {
          try {
            const suggestedResponse = await api.get(
              `${API_URLS.GET_GAME_PERIOD_SUGGESTED_TIMES}/${gameId}/periods/suggested-times`,
              { params: { clientId, seasonId } },
            );
            if (suggestedResponse.data.success && suggestedResponse.data.data.hasStats) {
              suggestedTimes = suggestedResponse.data.data.suggestedTimes;
            }
          } catch {
            // non-fatal
          }
        }

        // Fetch existing saved periods
        let existingPeriods: Period[] | null = null;
        if (gameId) {
          try {
            const periodsResponse = await api.get(`${API_URLS.GET_GAMES}/${gameId}/periods`, {
              params: { clientId, seasonId },
            });
            if (periodsResponse.data.success && periodsResponse.data.data.periods.length > 0) {
              existingPeriods = periodsResponse.data.data.periods.map((p: any, index: number) => ({
                id: index + 1,
                label: p.label,
                // Support both old endTime and new startTime field from DB
                startTime: p.startTime || p.endTime || '',
              }));
            }
          } catch {
            // no existing periods
          }
        }

        // Apply suggested times only when there are no saved periods
        const applySuggested = (base: Period[]): Period[] => {
          if (suggestedTimes.length === 0) return base;
          return base.map((p) => {
            const match = suggestedTimes.find((s) => s.label === p.label);
            return match ? { ...p, startTime: match.firstStatTime } : p;
          });
        };

        if (existingPeriods) {
          // Saved periods take priority — do not overwrite with suggested times
          setPeriods(existingPeriods);
        } else {
          setPeriods(applySuggested(buildDefaultPeriods(isHalfPeriod, gameOtFlag)));
        }
      } catch (error: any) {
        console.error('Error fetching game info:', error);
        showErrorMsg('Failed to fetch game information');
        setPeriods(buildDefaultPeriods(false, false));
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchGameInfo();
    }
  }, [clientId, gameId, seasonId]);

  const buildDefaultPeriods = (isHalfPeriod: boolean, withOT: boolean): Period[] => {
    const today = new Date().toISOString().split('T')[0];
    const base: Period[] = isHalfPeriod
      ? [
          { id: 1, label: 'H1', startTime: `${today}T00:00:00.000Z` },
          { id: 2, label: 'H2', startTime: `${today}T00:15:00.000Z` },
        ]
      : [
          { id: 1, label: 'Q1', startTime: `${today}T00:00:00.000Z` },
          { id: 2, label: 'Q2', startTime: `${today}T00:15:00.000Z` },
          { id: 3, label: 'Q3', startTime: `${today}T00:30:00.000Z` },
          { id: 4, label: 'Q4', startTime: `${today}T00:45:00.000Z` },
        ];

    if (withOT) {
      base.push({
        id: base.length + 1,
        label: 'OT',
        startTime: isHalfPeriod ? `${today}T00:30:00.000Z` : `${today}T00:20:00.000Z`,
      });
    }
    return base;
  };

  const addOTPeriod = async () => {
    let otStartTime: string;

    try {
      // Use the last stat time of the last regular period as the OT ceiling start time
      const response = await api.get(API_URLS.GET_MOST_RECENT_QUARTER, {
        params: { clientId, seasonId, gameId },
      });

      if (response.data.success && response.data.data?.lastEntryTime) {
        // Ceiling to next minute: e.g. 18:39:23.234 -> 18:40:00
        const d = new Date(response.data.data.lastEntryTime);
        if (d.getSeconds() > 0 || d.getMilliseconds() > 0) {
          d.setMinutes(d.getMinutes() + 1);
        }
        d.setSeconds(0);
        d.setMilliseconds(0);
        otStartTime = d.toISOString();
      } else {
        throw new Error('No stat time available');
      }
    } catch {
      // Fallback: use last regular period's startTime + 1 second
      const regularPeriods = periods.filter((p) => p.label !== 'OT');
      if (regularPeriods.length > 0) {
        const lastPeriod = regularPeriods[regularPeriods.length - 1];
        const d = new Date(lastPeriod.startTime);
        d.setSeconds(d.getSeconds() + 1);
        otStartTime = d.toISOString();
      } else {
        const today = new Date().toISOString().split('T')[0];
        otStartTime = gameType === 'halves' ? `${today}T00:30:00.000Z` : `${today}T00:20:00.000Z`;
      }
    }

    setPeriods((prev) => [...prev, { id: prev.length + 1, label: 'OT', startTime: otStartTime }]);
    setOtFlag(true);
    setDirty(true);
  };

  const removePeriod = (id: number) => {
    const periodToRemove = periods.find((p) => p.id === id);
    if (periodToRemove?.label === 'OT') {
      setPeriods(periods.filter((p) => p.id !== id));
      setOtFlag(false);
      setDirty(true);
    }
  };

  const updatePeriodStartTime = (id: number, localStartTime: string) => {
    const utcStartTime = localToUtc(localStartTime);
    setPeriods(periods.map((p) => (p.id === id ? { ...p, startTime: utcStartTime } : p)));
    setDirty(true);
  };

  const handleUpdatePeriods = async () => {
    try {
      if (!gameId) {
        showErrorMsg('Game ID is required');
        return;
      }

      setSaving(true);
      const hasOTPeriod = periods.some((p) => p.label === 'OT');

      const response = await api.put(`${API_URLS.UPDATE_GAME_PERIODS}/${gameId}/periods`, {
        clientId,
        seasonId,
        periods: periods.map((p) => ({
          label: p.label,
          startTime: p.startTime,
        })),
        otFlag: hasOTPeriod,
      });

      if (response.data.success) {
        setOtFlag(hasOTPeriod);

        await api.post(API_URLS.UPDATE_GAME_OT, {
          gameId,
          otFlag: hasOTPeriod,
        });

        const statsReclassified = response.data.data?.statsReclassified || 0;
        showSuccessMsg(
          statsReclassified > 0
            ? `Periods updated. ${statsReclassified} stat${statsReclassified !== 1 ? 's' : ''} reclassified.`
            : 'Periods updated successfully.',
        );
        setDirty(false);
      } else {
        showErrorMsg(response.data.message || 'Failed to update periods');
      }
    } catch (error: any) {
      console.error('Error updating periods:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to update periods');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <div className="flex flex-col justify-between text-sd-navy text-2xl sm:text-3x font-bold mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">Period Update</h1>
        <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium break-all">
          If a large number of adjustments need to be made in the game log, please reach out to our support team.
        </p>
      </div>

      <div className="pb-4">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-sd-navy text-lg">Loading game information...</div>
          </div>
        ) : (
          <>
            <div className="mb-6 shadow-lg">
              <div className="bg-sd-navy text-white text-lg font-semibold px-4 py-2">
                Period Start Times ({gameType === 'halves' ? 'Halves' : 'Quarters'})
              </div>

              <div className="bg-white border border-t-0 border-sd-line px-4 py-2 divide-y divide-[#e3f3ec]">
                {periods.map((period) => (
                  <div
                    key={period.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <label
                        className={`font-medium text-lg ${period.label === 'OT' ? 'text-gray-700' : 'text-gray-400'}`}
                      >
                        {period.label}
                      </label>
                      {period.label === 'OT' && (
                        <button
                          onClick={() => removePeriod(period.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded border border-red-300 hover:border-red-500"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      type="datetime-local"
                      step="1"
                      value={utcToLocal(period.startTime)}
                      onChange={(e) => updatePeriodStartTime(period.id, e.target.value)}
                      disabled={period.label !== 'OT'}
                      className={`w-full sm:w-64 text-right px-2 py-1 border rounded font-bold text-lg border-[#d6f5e6] ${
                        period.label === 'OT' ? 'bg-[#f3fcf7]' : 'bg-gray-200 cursor-not-allowed opacity-70'
                      }`}
                    />
                  </div>
                ))}

                {/* Add OT Period Button */}
                {!otFlag && (
                  <div className="flex justify-center py-3">
                    <button
                      onClick={addOTPeriod}
                      className="font-semibold py-2 px-6 rounded text-lg shadow-md transition-colors duration-200 bg-sd-navy text-white hover:bg-sd-navy"
                    >
                      Add OT Period
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center mt-4 mb-4">
              <button
                onClick={handleUpdatePeriods}
                disabled={saving || !dirty}
                className={`font-semibold py-2 px-8 rounded text-lg shadow-md transition-colors duration-200 ${
                  saving || !dirty
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-70'
                    : 'bg-sd-accent text-black hover:bg-[#6fd620]'
                }`}
              >
                {saving ? 'Updating...' : 'Update Periods'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PeriodsPage;
